// NOTES
//-------------------------------------------------------------------------------------------------------------------------
/*
    This is a simple set of validation tools using standard javascript.

    The validation tools provided by Angular 4 did not meet our needs.  We tried to create an angular 4 style validation library 
    of our own (field-validation directive) but ran into myriad conflicts with the Angular 4 lifecycle that caused the solution
    to be "hacky", bulky and unreliable.

    (This is no longer a service but an instantiatable object (should probably move it).  I did this because I have
    some future notes for this class that may require more than one injected instance per page.)


    // CVAL attributes examples
    //----------------------------
    data-cval-required="true"
    data-cval-numeric="true"
    data-cval-date="true"
    data-cval-numeric-min="5"
    data-cval-numeric-max="5"
    data-cval-starts-with="P"
    data-cval-range="100,200"
    data-cval-regex="^E[2-8]+|^[A-Z]$"
    data-cval-regex-flags="i"
    data-cval-msg-element-id="some-field-id"
    data-cval-fld-name="field display"              //If provided, the validation messages will use this instead of a generic field message
    data-cval-custom-fn="fnName"                    //Expected response from subscriber: { valid:<bool>, msg:<string> } //msg is displayed in data-cval-msg-element-id when valid=false


    //Note: In some cases you may have to use a special format to make angular happy

        [attr.data-cval-required]="true"

    // This may cause you trouble, because angular sometimes sanitizes (strips out) data-* elements and strips them out.
    // Look up "ngSanitize" if you can't find any other way around this situation.  That *might* be the right answer.

*/

// IMPORTS
//-------------------------------------------------------------------------------------------------------------------------
//Angular
//import { Injectable } from '@angular/core';
import moment from 'moment';


//Third Party
//import moment from 'moment';


// EXPORTS
//-------------------------------------------------------------------------------------------------------------------------
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
// CASValidate
//~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~~
export class CASValidate {

    // PRIVATE PROPERTIES
    //-------------------------------------------------------------------------------------------------------------------------
    _fnOnValidation = null;       //<fn(el, res)>:     Fired when a validation occurs (see validate method below for more details)
    _subscriber = null;

    // INITIALIZATION
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // constructor
    //==================================
    constructor(componentRef){
        if (componentRef) { this._subscriber = componentRef; }
    };

    
    // PUBLIC FUNCTIONS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // markAllValid
    //==================================
    // Marks all of the cval fields as valid even if they are not (will be updated next time they are changed)
    //
    //  idContainer<string>:    //(opt, null) If provided, only fields in this container will be cleared
    markAllValid(idContainer){

        let elements = [];
        if (idContainer) {
            elements = document.getElementById(idContainer).getElementsByClassName("cval-invalid"); //Returns a live HTMLCollection
        }
        else {
            elements = document.getElementsByClassName("cval-invalid"); //Returns a live HTMLCollection
        }

        //grab all elements currently marked as invalid
        //let elements = document.getElementsByClassName("cval-invalid");

        //This HTMLCollection is live (holy crap), so you can't just iterate with a for loop
        while (elements.length > 0){

            var el = elements[0];
            
            //Clear the invalid "flag"
            el.classList.remove("cval-invalid");

            //Remove any validation messages if an output element has been indicated
            let msgElementId = el.getAttribute("data-cval-msg-element-id");
            if (msgElementId){
                let elMsg = document.getElementById(msgElementId);
                if (elMsg){
                    elMsg.innerText = "";
                }
            }

        }

    };

    //==================================
    // isAllValid
    //==================================
    //Returns true if all fields on the page are valid
    //
    //  bRefresh<bool>:         //(opt, false) Pass true to force a re-validation of all fields before testing for validity
    //  idContainer<string>:    //(opt, null) If provided, only fields in this container will be validated
    //
    //  Note, calling this method with bRefresh=true from a function that is triggered by angular can cause issues, as the 
    //  refresh may alter the form.  This might trigger the very annoying angular "error" about changing "already checked" fields
    isAllValid(bRefresh, idContainer){
        
        bRefresh = bRefresh || false;

        if (bRefresh){
            //Trigger a fresh validation of all cval elements
            var elements = [];
            if (idContainer){
                elements = document.getElementById(idContainer).getElementsByClassName("cval"); //Returns a live HTMLCollection
            }
            else{
                elements = document.getElementsByClassName("cval"); //Returns a live HTMLCollection
            }
            
            for (var i=0; i<elements.length; i++){
            
                var el = elements[i];
                this.validate(el);
            }
        }

        if (idContainer) {
            return document.getElementById(idContainer).getElementsByClassName("cval-invalid").length == 0;
        }
        else {
            return document.getElementsByClassName("cval-invalid").length == 0;
        }
        
    };

    //==================================
    // initialize
    //==================================
    //  Looks for elements on the page with the classname "cval" and prepares them for validation
    //
    //      defer<bool>:              //(nullable, false) Set to true to force this method to execute at the "back of the stack" (timeout 0) 
    //      fn<function>:             //(nullable, null) Function to call when validation occurs (not needed for validation to work, its
    //                                                   provided just in case you need to do something else on validation)
    initialize(defer, fn){
        
        //Default the args
        defer = defer || false;
        this._fnOnValidation = fn || null;

        //We call defer if the target elements are in containers that don't display immediately
        //  (example: To give a dynamic popup a chance to render before trying to interrogate its elements)
        if (defer){
            setTimeout(()=>{ _initialize(); }, 0);
        }
        else{
            _initialize();
        }

        //Enclosure for the internal function
        var _this = this;

        //==================================
        // _initialize
        //==================================
        //Internal function
        function _initialize(){

            //Grab elements flagged for validation
            //let elements = document.getElementsByClassName("cval");
            var elements = Array.prototype.slice.call(document.getElementsByClassName("cval")); //returns an HTMLCollection

            //Process any elements marked for validation
            elements.map((el)=>{

                //Add the event listener 
                //  Issues with "this" context make relying on the addEventListener() to prevent dupes awkward
                //  I'm just going to mark them manually once I've added the handler (easy peasy)
                if (!el.getAttribute("data-cval-attached")){
                    el.setAttribute("data-cval-attached", 'true');
                    el.addEventListener("input", ()=>{ _this.validate(el); }, false);
                }
            });
        }

    };

    //==================================
    // validate
    //==================================
    //  Validate a given element (currently only tested on inputs)
    //      el<element>     //The element being validated
    //
    //  FIRES:
    //  _fnOnValidation(
    //      el, //element this validation was executed on
    //      res //results for the validation of this element
    //  )
    validate(el){

        let res = { valid:true, messages:[] };

        //Make sure we have an element
        if (!el){ throw("[casValidate:validate] event target element is null"); }

        // Validation Tests
        //-----------------------------------
        // This uses the string-extensions.js (toBoolean)

        //REQUIRED
        if (el.getAttribute("data-cval-required")){
            if (el.getAttribute("data-cval-required").toBoolean()) {
                this._validate_required(el, res); //res is modified
            }
        }

        //NUMERIC
        if (el.getAttribute("data-cval-numeric")){
            this._validate_numeric(el, res); //res is modified
        }

        //DATE
        if (el.getAttribute("data-cval-date")) {
            this._validate_date(el, res); //res is modified
        }

        //LENGTH
        //TODO (haven't needed it yet since most inputs have built in length settings)

        //REGEX-PATTERN
        if (el.getAttribute("data-cval-regex")) {
            this._validate_regex(el, res); //res is modified
        }

        //RANGE
        if (el.getAttribute("data-cval-range")) {
            this._validate_range(el, res); //res is modified
        }

        //STARTS WITH
        if (el.getAttribute("data-cval-starts-with")) {
            this._validate_starts_with(el, res); //res is modified
        }

        //SELECTED INDEX (for dropdown)
        if (el.getAttribute("data-cval-dropdown-selected")) {
            this._validate_dropdown(el, res); //res is modified
        }

        //CUSTOM FN
        //Expected response from subscriber: { valid:<bool>, msg:<string> }
        if (el.getAttribute("data-cval-custom-fn")) {
            this._validate_custom_fn(el, res); //res is modified
        }


        //Update the field
        if (res.valid){
            el.classList.remove("cval-invalid");
        }
        else{
            el.classList.add("cval-invalid");
        }

        //Check to see if there is a validation message output div defined
        let msgElementId = el.getAttribute("data-cval-msg-element-id");
        if (msgElementId){
            let elMsg = document.getElementById(msgElementId);
            if (elMsg){
                elMsg.innerText = res.valid ? "" : res.messages.join("\r\n");
            }
        }

        //See if we have a callback to fire
        if (this._fnOnValidation){
            this._fnOnValidation(el, res);
        }
    };

    // PRIVATE FUNCTIONS
    //-------------------------------------------------------------------------------------------------------------------------
    //==================================
    // _validate_regex
    //==================================
    //impure: modifies res
    _validate_regex(el, res) {

        //Default the value
        let val = el.value || null;

        //If there is no value, let just not test here.  We can use required for that instead.
        //Otherwise we might get multiple error messages
        if (!val) { return; }

        //Grab the regex
        let pattern = el.getAttribute("data-cval-regex") || null;
        let flags = el.getAttribute("data-cval-regex-flags") || "";

        //If there is no value in the regex field, just jump out without any tests (don't invalidate the element)
        if (!pattern) { return; }

        let re = new RegExp(pattern, flags);

        //See if the field value matches the regex
        if (!re.test(val)) {

            res.valid = false;

            //Use the cval-fld-name if provided
            var fldName = el.getAttribute("data-cval-fld-name") || "Value";
            res.messages.push("{0} is invalid".format(fldName));
        }
    };

    //==================================
    // _validate_range
    //==================================
    //impure: modifies res
    _validate_range(el, res) {

        //Default the value
        var val = el.value || null;

        //If there is no value, let's not test here.  We can use required for that instead.
        //Otherwise we might get multiple error messages
        if (!val) { return; }

        //Grab the range
        let range = el.getAttribute("data-cval-range") || null;

        //If there is no value in the range field, just jump out without any tests (don't invalidate the element)
        if (!range) { return; }

        let lower = Math.min(range.split(',')[0]), upper = Math.max(range.split(',')[1]);

        // Remove text values after numbers to check for starts with in range
        if (!Number(val)) {
            val = val.replace(/(^\d+)(.+$)/i, '$1');
        }

        //See if the field value matches the range
        if (!(val >= lower && val <= upper)) {

            res.valid = false;

            //Use the cval-fld-name if provided
            var fldName = el.getAttribute("data-cval-fld-name") || "Value";
            res.messages.push("{0} is invalid, must be between {1} and {2}.".format(fldName, lower, upper));
        }
    };

    //==================================
    // _validate_starts_with
    //==================================
    //impure: modifies res
    _validate_starts_with(el, res) {

        //Default the value
        var val = el.value || null;

        //If there is no value, let's not test here.  We can use required for that instead.
        //Otherwise we might get multiple error messages
        if (!val) { return; }

        //Grab the starts_with
        let starts_with = el.getAttribute("data-cval-starts-with") || null;

        //If there is no value in the starts_with field, just jump out without any tests (don't invalidate the element)
        if (!starts_with) { return; }

        //See if the field value matches the starts_with
        if (!val.startsWith(starts_with)) {

            res.valid = false;

            //Use the cval-fld-name if provided
            var fldName = el.getAttribute("data-cval-fld-name") || "Value";
            res.messages.push("{0} is invalid, must start with {1}".format(fldName, starts_with));
        }
    };

    //==================================
    // _validate_numeric
    //==================================
    //impure: modifies res
    _validate_numeric(el, res) {

        //Use the cval-fld-name if provided
        var fldName = el.getAttribute("data-cval-fld-name") || "Value";

        //The element value should not be null, but just in case it ever happens treat it like an empty string
        let val = el.value || "";

        //First test for numeric
        if (isNaN(val)) {
            res.valid = false;
            res.messages.push("{0} expected to be numeric".format(fldName));
        }

        //Jump out if we already have an invalid value (can't do num compares)
        if (!res.valid) { return; }

        //Next check to see if any ranges are provided
        let min = el.getAttribute("data-cval-numeric-min"); //null if not found
        let max = el.getAttribute("data-cval-numeric-max"); //null if not found

        if (min != null) {
            if (+val < +min) {
                res.valid = false;
                //res.messages.push("Value expected to be " + min + " or greater");
                res.messages.push("{0} expected to be {1} or greater".format(fldName, min));

            }
        }

        if (max != null) {
            if (+val > +max) {
                res.valid = false;
                //res.messages.push("Value expected to be " + max + " or less");
                res.messages.push("{0} expected to be {1} or less".format(fldName, max));
            }
        }
    };

    //==================================
    // _validate_required
    //==================================
    //impure: modifies res
    _validate_required(el, res){

        //The element value should not be null, but just in case it ever happens treat it like an empty string
        let val = el.value || "";

        //Check to see if the field has a value
        if (val.trim().length == 0){
            res.valid = false;

            //Use the cval-fld-name if provided
            var fldName = el.getAttribute("data-cval-fld-name") || "Value";
            res.messages.push("{0} is required".format(fldName));
        }

    };

    //==================================
    // _validate_dropdown
    //==================================
    //impure: modifies res
    _validate_dropdown(el, res) {

        //The element selected index should be > 0 (in the case of emtpy first row)
        let val = el.selectedIndex;

        //Check to see if the field has a value
        if (val < 1) {
            res.valid = false;

            //Use the cval-fld-name if provided
            var fldName = el.getAttribute("data-cval-fld-name") || "Value";
            res.messages.push("{0} is required".format(fldName));
        }

    };

    //==================================
    // _validate_date
    //==================================
    //impure: modifies res
    _validate_date(el, res) {

        //The element value should not be null, but just in case it ever happens treat it like an empty string
        let val = el.value || "";

        //Use the cval-fld-name if provided
        let fldName = el.getAttribute("data-cval-fld-name") || "Value";

        //If there is no value, let just not test here.  We can use required for that instead.
        //Otherwise we might get multiple error messages
        if (!val) { return; }

        // Validate as a date?
        //----------------------- 
        let isValid = !(val.length > 10);
        if (!isValid) {
            res.valid = false;
            res.messages.push("{0} is invalid because it's too long".format(fldName));
            return;
        }
        isValid = new Date(val).valueOf() > new Date("12/31/1899").valueOf();
        if (!isValid) {
            res.valid = false;
            res.messages.push("{0} is invalid".format(fldName));
            return;
        }
    };

    //==================================
    // _validate_custom_fn
    //==================================
    _validate_custom_fn(el, res) {

        if (!this._subscriber) {
            console.log("Cannot execute a custom validation function without the component context");
        }

        //Grab the custom function (this is in string form at this point)
        let fnString = el.getAttribute("data-cval-custom-fn");

        //Execute the function in the proper context
        let subscriberRes = { valid:true, msg:"" };
        if (typeof this._subscriber[fnString] === 'function') {
            subscriberRes = this._subscriber[fnString](el); //Expected response: { valid:<bool>, msg:<string> }
        }
        else {
            throw("The function name provided by data-cval-custom-fn cannot be found on the subscribing component.");
        }

        //Capture the response result
        res.valid = subscriberRes.valid;

        if (!res.valid) {
            res.messages.push(subscriberRes.msg);
        }
    }


}
